import { useLocation, useNavigate } from "react-router-dom";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { SSN_PRIVACY_POLICY_SECTION } from "@/portals/patient/pages/profile/ssnConsentCopy";

interface LegalSection {
  title: string;
  body: string;
}

const privacySections: LegalSection[] = [
  {
    title: "Information We Collect",
    body: "We collect account details such as your name, email address, password credentials, profile information, contact details you choose to provide, and, only if you choose to give it, your Social Security Number (see the Social Security Number section below). When you use MedSyncIQ, we may also collect uploaded health records, document metadata, usage activity, device information, and support messages needed to operate the platform.",
  },
  {
    title: "How We Use Your Information",
    body: "We use your information to create and manage your account, help you upload and organize medical records, generate health-record insights, support authentication and security features, respond to requests, and improve the reliability and usability of the platform.",
  },
  {
    title: "Health Records And Sensitive Data",
    body: "Medical records and related health information are handled with added care. MedSyncIQ is designed to support privacy and security practices expected for health information, including access controls, secure transmission, and limited use of data for the services you request.",
  },
  SSN_PRIVACY_POLICY_SECTION,
  {
    title: "Data Security",
    body: "We use technical, administrative, and operational safeguards to help protect your data. No system can guarantee absolute security, but we work to reduce risk through encryption, authenticated access, secure storage practices, and monitoring.",
  },
  {
    title: "Sharing Your Information",
    body: "We do not sell your personal information. We may share information with service providers that help run the platform, when required by law, or when you choose to share records with a provider, caregiver, or another authorized party.",
  },
  {
    title: "Your Choices",
    body: "You may update your profile information, change your email address through verification, add or remove your optional SSN, manage uploaded records, or request account deletion where available. Some information may need to be retained for security, legal, or operational reasons.",
  },
  {
    title: "Contact",
    body: "If you have questions about this Privacy Policy or how your information is handled, contact the MedSyncIQ support team through the official support channel.",
  },
];

const termsSections: LegalSection[] = [
  {
    title: "Use Of MedSyncIQ",
    body: "MedSyncIQ is provided to help users upload, organize, and understand health-record information. You agree to use the platform only for lawful purposes and in a way that does not interfere with the service or other users.",
  },
  {
    title: "Account Responsibilities",
    body: "You are responsible for keeping your login credentials secure and for the activity that occurs under your account. Notify us promptly if you believe your account has been accessed without permission.",
  },
  {
    title: "Information You Provide",
    body: "You agree that information you provide is accurate to the best of your knowledge. You should only upload records or documents that you have the right to use or manage.",
  },
  {
    title: "No Emergency Or Medical Advice",
    body: "MedSyncIQ does not replace professional medical advice, diagnosis, treatment, or emergency care. Always consult qualified healthcare professionals for medical decisions, and contact emergency services for urgent situations.",
  },
  {
    title: "Platform Insights",
    body: "Any summaries, explanations, or insights are provided to help you better review your records. They may be incomplete or imperfect and should be checked against the original documents and professional guidance.",
  },
  {
    title: "Service Changes",
    body: "We may update, improve, suspend, or discontinue parts of the platform as needed. We may also update these Terms from time to time, and continued use means you accept the updated terms.",
  },
  {
    title: "Termination",
    body: "We may restrict or terminate access if an account is used in a way that violates these Terms, creates security risk, or harms the platform or other users.",
  },
];

const hipaaSections: LegalSection[] = [
  {
    title: "Our Commitment To Your Health Information",
    body: "This notice describes how medical information about you may be used and disclosed, and how you can access this information. MedSyncIQ is required by law to maintain the privacy of your protected health information (PHI), provide this notice of our legal duties and privacy practices, and follow the terms currently in effect.",
  },
  {
    title: "How We May Use And Disclose Your Information",
    body: "We may use and disclose your health information to operate the platform's core functionality: parsing, normalizing, and storing uploaded records so you can review them; to power the AI assistant when that feature is enabled; and to provide customer support you request. We do not use or disclose your health information for marketing, advertising, or sale to third parties.",
  },
  {
    title: "Uses And Disclosures Requiring Your Authorization",
    body: "Other than the operational uses described above, we will not disclose your protected health information to any third party without your explicit authorization. This includes any voluntary export or sharing you initiate yourself, such as downloading a FHIR-standard file to give to a doctor or hospital. Those actions are always patient-initiated and never automatic.",
  },
  {
    title: "Your Rights Regarding Your Health Information",
    body: "You have the right to review and receive a copy of your health information, request corrections to information you believe is inaccurate or incomplete, request an accounting of certain disclosures, request restrictions on certain uses and disclosures, and request confidential communications. You may exercise these rights at any time through your account settings or by contacting us.",
  },
  {
    title: "Security Safeguards",
    body: "We maintain administrative, technical, and physical safeguards designed to protect the confidentiality, integrity, and availability of your health information, including encryption in transit and at rest, access controls, and activity logging for consent and permission changes.",
  },
  {
    title: "Changes To This Notice",
    body: "We reserve the right to change this notice and to make the revised notice effective for health information we already have as well as any information we receive in the future. Updates will be posted here with a new effective date.",
  },
  {
    title: "Complaints",
    body: "If you believe your privacy rights have been violated, you may file a complaint with us or with the U.S. Department of Health and Human Services. You will not be penalized for filing a complaint. Contact the MedSyncIQ support team through the official support channel to raise a concern.",
  },
];

function LegalPage({ title, sections }: { title: string; sections: LegalSection[] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { from?: string; signupForm?: Record<string, unknown> } | null;
  const backTo = state?.from ?? "/patient/auth/signup";

  return (
    <main className="min-h-screen bg-[#EEF1F4] px-4 py-5">
      <section className="mx-auto flex max-h-[calc(100vh-40px)] max-w-[1180px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_18px_45px_rgba(17,24,39,0.18)]">
        <div className="border-b border-[#EEF0F3] px-8 py-5 text-center">
          <h1 className="text-xl font-extrabold text-[#180047]">{title}</h1>
        </div>

        <div className="overflow-y-auto px-8 py-2 [scrollbar-color:#F27F95_#D9D9D9] [scrollbar-width:thin]">
          <div className="bg-[#FCFCFD] px-4 pb-8">
            {sections.map((section) => (
              <section key={section.title} className="border-b border-[#180047]/70 py-7">
                <h2 className="text-[30px] font-extrabold leading-tight text-[#F16F8A]">
                  {section.title}
                </h2>
                <p className="mt-6 text-base leading-6 text-[#747980]">{section.body}</p>
              </section>
            ))}
          </div>
        </div>

        <div className="flex justify-center border-t border-[#EEF0F3] px-8 py-4">
          <PageBackButton onClick={() => navigate(backTo, { state: { signupForm: state?.signupForm } })} ariaLabel="Go back" />
        </div>
      </section>
    </main>
  );
}

export function PrivacyPolicyPage() {
  return <LegalPage title="Privacy Policy" sections={privacySections} />;
}

export function TermsOfUsePage() {
  return <LegalPage title="Terms of Use" sections={termsSections} />;
}

export function HipaaNoticePage() {
  return <LegalPage title="HIPAA Notice of Privacy Practices" sections={hipaaSections} />;
}
